// Imports
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { darken } from 'polished';
import { ReactNode } from 'react';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import { GeneratedThemeProps } from '../../templates/template-style';
import ProfilePhoto from '../shared-components/profile-photo';
import UserAvatar from '../shared-components/user-avatar';
import memberNavigationItems from './member-navigation-items';


// Styled component
const Container = styled.button<{ opened: boolean; theme: GeneratedThemeProps }>`
	grid-column: 2;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding-top: 0.3125rem;
	padding-bottom: 0.25rem;
	
	background: none;
	border: none;
	color: ${(props) => (props.theme.isPaper ? '#000' : '#fff')};
	position: relative;
	
	font-family: inherit;
	font-size: 0.8125rem;
	
	${(props) =>
		props.opened &&
		css`
			background: #fff;
			color: #000;
		`}
`;

const ProfileAvatar = styled(UserAvatar)<{ opened: boolean; theme: GeneratedThemeProps }>`
	display: flex;
	align-items: center;
	justify-content: center;
	
	height: 1.3rem;
	width: 1.3rem;
	font-size: 0.7rem;
	
	box-shadow: ${({ opened, theme }) =>
		theme.isPaper || opened ? '0 0 0.25rem rgba(0, 0, 0, 0.3)' : '0 0 0.25rem rgba(0, 0, 0, 0.3)'};
	
	background-color: #fff;
	color: ${({ theme }) => (theme.isPaper ? '#000' : darken(0.08, theme.baseColor))};
`;

const DropdownMenu = styled.div`
	min-width: 12.5rem;
	padding: 0.125rem 0.875rem 0.3125rem;
	background: #fff;
	border-bottom-left-radius: 0.1875rem;
	border-bottom-right-radius: 0.1875rem;
	line-height: 1.375rem;
	
	color: #000;
	box-shadow: 0 0.5rem 0.5rem rgba(0, 0, 0, 0.25);
	position: absolute;
	cursor: default;
	right: 0;
	z-index: 99;
	top: 100%;
	
	background: #fff;
	text-align: right;
	z-index: 99;
	
	ul {
		list-style-type: none;
		
		li a {
			color: #000;
			
			.svg-inline--fa {
				margin-left: 0.3125rem;
			}
		}
	}
`;


// Define the accepted props
interface Props {
	storybookIsUserDropdownOpen?: true;
}


// Function component
const UserBox: React.FC<Props> = ({ storybookIsUserDropdownOpen }) => {
	// Use Redux functionality
	const user = useAppSelector((state) => state.common.user);
	const member = useAppSelector((state) => state.self.member);
	const website = useAppSelector((state) => state.self.website);
	
	
	// Get array of drop down items
	const items = website && user ? memberNavigationItems(member, website) : [];
	
	
	// Use accessible dropdown
	const { buttonProps, itemProps, isOpen } = useDropdownMenu(items.length);
	
	
	// Initialize
	const dropdownMenuList: ReactNode[] = [];
	
	
	// Iterate over items
	for (const [index, item] of items.entries()) {
		dropdownMenuList.push(
			<li key={item.uniqueKey}>
				{item.type === 'link' ? (
					<Link {...itemProps[index]} to={item.to}>
						{item.content}
						<FontAwesomeIcon icon={item.icon} fixedWidth />
					</Link>
				) : (
					<a {...itemProps[index]} href={item.to}>
						{item.content}
						<FontAwesomeIcon icon={item.icon} fixedWidth />
					</a>
				)}
			</li>
		);
	}
	
	
	// Return JSX
	return (
		<Container {...buttonProps} aria-label='Member dropdown menu' opened={storybookIsUserDropdownOpen ?? isOpen}>
			{user.profilePhotoURL ? (
				<ProfilePhoto alt='Profile photo' src={user.profilePhotoURL} size='1.3rem' />
			) : (
				<ProfileAvatar
					aria-label='Profile photo'
					firstName={user.firstName ?? ''}
					lastName={user.lastName ?? ''}
					round
					opened={storybookIsUserDropdownOpen ?? isOpen}
				/>
			)}
			Welcome, {member?.names.member ?? user.name}
			<FontAwesomeIcon icon={faChevronDown} />
			{(storybookIsUserDropdownOpen ?? isOpen) && (
				<DropdownMenu role='menu'>
					<ul>{dropdownMenuList}</ul>
				</DropdownMenu>
			)}
		</Container>
	);
};

export default UserBox;
